/* rb-b-persist.js — keep the shopper's facet arrangement across filter clicks.

   Loaded AFTER rb-b-sidebar.js. Separate file on purpose: rb-b-sidebar.js is
   ~20KB and every edit means retyping it into a GraphQL payload, which is the
   transcription risk being avoided throughout this project.

   THE PROBLEM
   Every filter click makes DynamicProductSearch re-render the section from
   Liquid, so the sidebar returns at the theme's defaults — filter_visibility
   'three' opens the first three groups and the size group carries a hard-coded
   `open` in main-collection-product-grid.liquid. Whatever the shopper arranged
   is wiped on every tag they pick.

   SIX BUGS GOT FIXED HERE, EVERY ONE FOUND BY LOGGING RATHER THAN REASONING.
   Read these before changing anything — most of them look correct in review:

   1. Recording on the raw 'toggle' event captured the THEME's programmatic
      re-opening of the size group as the shopper's choice, overwriting the
      collapse a moment after restoring it.

   2. Recording on click with setTimeout(...,0) read the value TOO EARLY — a
      <details> has not flipped yet at that point:
           click   size openAtClick=true
           record  size=true          <- wrong, it was being collapsed
           restore size false -> true <- this code re-opening it

   3. Restoring only inside a timed window after the swap still lost the size
      group, so the correction became self-healing: an observer on the `open`
      ATTRIBUTE undoes any disagreeing change whenever it lands.

   4. A single `armed` element could only remember ONE pending click, so
      clicking several summaries in the same tick recorded only the last.

   5. The self-healing observer then broke CLOSING a facet. Ordering: the click
      removes `open` -> the attribute observer runs as a microtask, BEFORE the
      'toggle' event is delivered -> the stored state is still the OLD value, so
      fix() re-opened the group and the toggle then recorded it as open. It
      could never be closed again. The observer now skips any group the shopper
      is mid-click on.

   6. The disarm timer was shared per element, so a SECOND click inside 400ms
      was cancelled by the FIRST click's timer: the group got un-armed just as
      its toggle arrived, that toggle was ignored as "programmatic", and the
      observer restored the stale value. Symptom: Brand and Price opened but
      refused to close on a quick second click. Each click now owns its own
      timer and re-arming clears the previous one.

   HOW STATE IS CAPTURED: a click on a <summary> arms that group; the very next
   'toggle' on the same element is therefore user-driven, and its post-flip
   value is the one to keep. 'toggle' does not bubble, so the listeners use the
   CAPTURE phase, which still sees non-bubbling events on the way down.

   Keyed on the group LABEL, not the id: the id is
   `{{ section.id }}-filter-{{ forloop.index }}`, which is positional and
   silently repoints at a different facet when Shopify returns filters in a
   different order as counts change. textContent, not innerText — the CSS
   uppercases the label, so innerText reads "SIZE" and textContent reads "Size". */

(function () {
  'use strict';

  if (!window.matchMedia('(min-width: 981px)').matches) return;

  var state = {};
  var armed = [];        // [{ el, timer }] — groups awaiting their toggle
  var writing = false;   // true while WE change `open`, so we ignore our own work

  function key(grp) {
    var s = grp.querySelector('summary span');
    return (((s && s.textContent) || grp.id || '') + '').trim().toLowerCase();
  }

  function isGroup(el) {
    return el && el.classList && el.classList.contains('collection-sidebar__filter-group');
  }

  function indexOfEl(el) {
    for (var i = 0; i < armed.length; i++) if (armed[i].el === el) return i;
    return -1;
  }

  function isArmed(el) { return indexOfEl(el) !== -1; }

  function disarm(el) {
    var i = indexOfEl(el);
    if (i === -1) return;
    clearTimeout(armed[i].timer);
    armed.splice(i, 1);
  }

  function arm(el) {
    disarm(el);            // bug 6: a new click must cancel the old timer
    var entry = { el: el, timer: null };
    entry.timer = setTimeout(function () { disarm(el); }, 400);   // no toggle came
    armed.push(entry);
  }

  /* ---- capture the shopper's intent ---- */
  document.addEventListener('click', function (e) {
    var s = e.target && e.target.closest && e.target.closest('summary');
    if (!s) return;
    var g = s.parentElement;
    if (!isGroup(g)) return;
    arm(g);
  }, true);

  document.addEventListener('toggle', function (e) {
    var g = e.target;
    if (!isGroup(g)) return;
    if (!isArmed(g)) return;      // programmatic toggle — not the shopper
    disarm(g);
    var k = key(g);
    if (k) state[k] = g.open;     // read AFTER the flip: correct value
  }, true);

  /* ---- enforce it ---- */
  function fix(g) {
    var k = key(g);
    if (!k || !(k in state)) return;   // never touched: keep the theme default
    if (g.open === state[k]) return;
    writing = true;
    g.open = state[k];
    writing = false;
  }

  function restoreAll() {
    document.querySelectorAll('.collection-sidebar__filter-group').forEach(function (g) {
      if (isArmed(g)) return;          // the shopper is mid-click on it
      fix(g);
    });
  }

  if (!window.MutationObserver) return;

  /* Self-healing: whoever flips `open` — the theme, an accordion script, a late
     re-render — is corrected immediately, with no timing window to guess at.
     Guarded by `writing` so our own correction cannot re-trigger this, and by
     the armed list so it never overrules a click in progress. */
  new MutationObserver(function (records) {
    if (writing) return;
    for (var i = 0; i < records.length; i++) {
      var t = records[i].target;
      if (!isGroup(t)) continue;
      if (isArmed(t)) continue;        // human mid-click; the toggle settles it
      fix(t);
    }
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['open'],
    subtree: true
  });

  /* A swap inserts brand new <details> elements already carrying `open`, which
     is a childList change rather than an attribute change, so it needs its own
     trigger. The delayed second pass covers the theme re-rendering twice. */
  var section = document.querySelector('.js-section__collection');
  if (section) {
    new MutationObserver(function () {
      if (writing) return;
      restoreAll();
      setTimeout(restoreAll, 120);
    }).observe(section, { childList: true, subtree: true });
  }
})();
